import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Edit2,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  Clock,
} from "lucide-react";
import type { ClaimStatusTemplate } from "@/api/generated/client/models/ClaimStatusTemplate";
import type { TaskTemplate } from "@/api/generated/client/models/TaskTemplate";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface StatusCardProps {
  status: ClaimStatusTemplate & { taskTemplates?: TaskTemplate[] };
  onEdit: (status: ClaimStatusTemplate) => void;
  onDelete: (status: ClaimStatusTemplate) => void;
  onAddTask: (status: ClaimStatusTemplate) => void;
  onEditTask: (task: TaskTemplate, status: ClaimStatusTemplate) => void;
  onDeleteTask: (task: TaskTemplate, status: ClaimStatusTemplate) => void;
  isAdmin: boolean;
}

export function StatusCard({
  status,
  onEdit,
  onDelete,
  onAddTask,
  onEditTask,
  onDeleteTask,
  isAdmin
}: StatusCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const tasks = status.taskTemplates || [];
  const sortedTasks = [...tasks].sort((a, b) => {
    // Sort by dueDaysOffset ascending
    const dueA = a.dueDaysOffset ?? Number.MAX_SAFE_INTEGER;
    const dueB = b.dueDaysOffset ?? Number.MAX_SAFE_INTEGER;
    if (dueA !== dueB) return dueA - dueB;
    // Fallback to displayOrder
    return a.displayOrder - b.displayOrder;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 hover:bg-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 hover:bg-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 hover:bg-green-200';
      default: return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="bg-card border rounded-lg shadow-sm"
    >
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-4 flex-1">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="p-0 hover:bg-transparent">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </CollapsibleTrigger>

          <div className="flex items-center gap-3">
            {/* Color Bubble */}
            <div
              className="h-4 w-4 rounded-full border shadow-sm"
              style={{ backgroundColor: status.color || '#e2e8f0' }}
            />

            {/* Title */}
            <h4 className="font-semibold text-lg">
              {status.displayName}
            </h4>

            {/* Subtasks Count Bubble */}
            <Badge variant="secondary" className="rounded-full px-2.5">
              {tasks.length}
            </Badge>
          </div>
        </div>

        {/* Right Aligned Actions */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onAddTask(status)}
              title="Add Task"
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(status)}
              title="Edit Status"
            >
              <Edit2 className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(status)}
              className="text-destructive hover:text-destructive"
              title="Delete Status"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <CollapsibleContent>
        <div className="px-4 pb-4 space-y-3">
          {/* Status Description (if exists) */}
          {status.description && (
            <p className="text-sm text-muted-foreground mb-4 pl-10 border-l-2">
              {status.description}
            </p>
          )}

          {/* Subtasks List */}
          <div className="pl-10 space-y-3">
            {sortedTasks.length > 0 ? (
              sortedTasks.map((task) => (
                <div
                  key={task.id}
                  className="bg-muted/30 border rounded-md p-3 space-y-2 hover:bg-muted/50 transition-colors"
                >
                  {/* Row 1: Title, Priority, Actions */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={cn("font-medium text-sm truncate")}>
                        {task.title}
                      </span>
                      <Badge
                        variant="secondary"
                        className={cn("text-[10px] px-1.5 py-0 h-5 font-normal capitalize", getPriorityColor(task.priority))}
                      >
                        {task.priority || 'normal'}
                      </Badge>
                    </div>

                    {isAdmin && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => onEditTask(task, status)}
                        >
                          <Edit2 className="h-3 w-3 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={() => onDeleteTask(task, status)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Row 2: Description */}
                  {task.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {task.description}
                    </p>
                  )}

                  {/* Row 3: Due Date */}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>
                      {task.dueDaysOffset !== null && task.dueDaysOffset !== undefined
                        ? `Due in ${task.dueDaysOffset} day${task.dueDaysOffset !== 1 ? 's' : ''}`
                        : "No due date"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-muted-foreground italic py-2">
                No tasks defined for this status
              </div>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
